import {LeftOutlined} from '@ant-design/icons';
import {Button, Card, Divider, message} from 'antd';
import {useEffect, useMemo} from 'react';
import toast from 'react-hot-toast';
import {MdAddShoppingCart, MdDeleteOutline} from 'react-icons/md';
import {useDispatch, useSelector} from 'react-redux';
import {Link, useNavigate} from 'react-router-dom';

import {RqGetAllProducts} from '../../reactQuery/apis';
import {removeFromCart, updateQuantity} from '../../redux/authAction';

const CartScreen = () => {
  const dispatch = useDispatch();
  
  const navigate = useNavigate();

  const {data: products, refetch} = RqGetAllProducts();

  useEffect(() => {
    refetch();
  }, []);

  const cart = useSelector(state => state.cart);

  const productSizeQuantityMap =useMemo(() => {
    if (!products) {return {};}
    const map = {};
    products.forEach(product => {
      map[product.id] = {};
      product.size?.forEach(sizeObj => {
        map[product.id][sizeObj.size] = Number(sizeObj.quantity);
      });
    });
    return map;
  }, [products]);

  const handleIncreaseQuantity = item => {
    const availableQuantity =
    productSizeQuantityMap[item.productId]?.[item.size] ?? 0;

    if (item.quantity < availableQuantity) {
      dispatch(
        updateQuantity({
          productId: item.productId,
          size: item.size,
          quantity: item.quantity + 1,
        }),
      );
    } else {
      toast.error(
        `Only ${availableQuantity} item(s) available for size ${item.size}`,
      );
    }
  };

  const handleDecreaseQuantity = item => {
    if (item.quantity > 1) {
      dispatch(updateQuantity({productId: item.productId, size: item.size, quantity: item.quantity - 1}));
    }
  };

  const getTotal = () => {
    let total = 0;
    cart.items.forEach(item => {
      total += item.pricePerUnit * item.quantity;
    });
    return total;
  };

  const isOutOfStock = item => {
    const availableQuantity = productSizeQuantityMap[item.productId]?.[item.size] ?? 0;
    return item.quantity > availableQuantity;
  };

  const isCheckoutDisabled = () => {
    return cart.items.some(item => isOutOfStock(item));
  };

  return (
    <div className={'w-full px-4 lg:px-20 py-4 flex lg:flex-row flex-col justify-center items-center gap-4 relative'}>
      <div className={'max-w-6xl w-full'}>
        {cart.items.length === 0 ? (
          <div className={'col-span-full flex flex-col items-center justify-center h-80'}>
            <h1 className={'font-semibold text-primary-900 lg:text-xl font-serif-custom'}>
            Your cart is empty...!
            </h1>
            <Link to={'/product'}>
              <Button className={'mt-5 group w-56 h-8 rounded-lg border-none bg-primary-900 flex items-center justify-center cursor-pointer transition duration-500 overflow-hidden shadow-md relative active:scale-95 text-sm hover:shadow-[0_0_5px_#3D180F]'}>
                <div className={'absolute left-[-50px] w-[30px] h-[30px] bg-transparent rounded-full flex items-center justify-center overflow-hidden z-[2] transition duration-500 group-hover:translate-x-[58px] group-hover:rounded-[40px]'}>
                  <div className={'rounded-sm text-primary-900 group-hover:text-[#3D180F] transition duration-500'}>
                    <MdAddShoppingCart className={'lg:ml-4'} />
                  </div>
                </div>
                <span className={'h-full w-fit flex items-center justify-center text-white z-[1] transition duration-500 text-[1.04em] font-semibold group-hover:text-[#3D180F] group-hover:translate-x-[10px]'}>
                Continue Shopping
                </span>
              </Button>
            </Link>
          </div>
        ) : (
          <div className={'grid grid-cols-1 lg:grid-cols-12 lg:gap-5 w-full mx-auto p-4 lg:p-0'}>
            <div className={'lg:col-span-12 mt-5'}>
              <Link to={'/product'}>
                <Button color={'default'} variant={'link'} className={'p-0 !text-primary-900 font-semibold mb-3 lg:text-lg'}>
                  <LeftOutlined /> Continue Shopping
                </Button>
              </Link>
            </div>
            <div className={'lg:col-span-7'}>
              {cart.items.map((item, index) => {
                const product = products?.find(p => p.id === item.productId);
  
                const latestSizeData = product?.size?.find(s => s.size === item.size);

                const latestQuantityAvailable = latestSizeData ? Number(latestSizeData.quantity) : 0;

                return (
                  <Card key={index} className={'mb-2 w-full shadow-md max-h-[190px] my-auto'}>
                    <div className={'flex flex-row justify-between'}>
                      <div className={'flex flex-row'}>
                        <div className={'w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-36 lg:h-36 '}>
                          <img alt={'images'} src={Array.isArray(item.imageUrl) ? item.imageUrl[0] : item.imageUrl} className={'w-full h-full object-cover rounded-lg'} />
                        </div>
                        <div className={'ml-4 lg:font-medium'}>
                          <h1 className={'font-semibold lg:text-base text-primary-900'}>&#8377; {item.pricePerUnit}</h1>
                          <h1 className={'text-sm'}>{item.productName}</h1>
                          <h1>Size: {item.size === null ? '-' : typeof item.size === 'object' ? item.size.size : item.size}</h1>
                          {latestQuantityAvailable <= 0 ? (
                            <p className={'text-[#FF0000] text-sm'}>Out of Stock</p>
                          ) : latestQuantityAvailable < item.quantity ? (
                            <p className={'text-[#FF0000] text-sm'}>
                            You can buy only {latestQuantityAvailable} products!
                            </p>
                          ) : latestQuantityAvailable <= 5 ? (
                            <p className={'text-[#FF0000] text-sm'}>
                             Only {latestQuantityAvailable} remaining!
                            </p>
                          ) : (
                            <p className={'text-primary-900 text-sm'}>In Stock</p>
                          )}
                         
                          <div className={'flex flex-row items-center lg:space-x-3 space-x-2 lg:mt-4 mt-2'}>
                            <Button
                              onClick={() => handleDecreaseQuantity(item)}
                              disabled={item.quantity === 1}
                              className={'w-3 h-6 rounded-lg !bg-primary-100 border-none hover:!border-gray-400 hover:!bg-gray-300'}
                            >
                              <h1 className={'text-black'}>-</h1>
                            </Button>
                            <h1 className={'text-lg font-semibold'}>{item?.quantity}</h1>
                            <Button
                              onClick={() => handleIncreaseQuantity(item)}
                              className={'w-3 h-6 rounded-lg !bg-primary-100 border-none hover:!border-gray-400 hover:!bg-gray-300'}
                            >
                              <h1 className={'text-black'}>+</h1>
                            </Button>
                          </div>
                        </div>
                      </div>
                      <div className={'flex flex-col items-end lg:font-medium'}>
                        <MdDeleteOutline
                          onClick={() => dispatch(removeFromCart({productId: item.productId, size: item.size}))}
                          className={'text-lg text-primary-900 cursor-pointer lg:mt-2'}
                        />
                        <h1 className={'text-sm flex'}>Qty: <span>{item.quantity}</span> </h1>
                      </div>
                    </div>
                  </Card>
                );
              })}  
            </div>

            <div className={'lg:col-span-5 lg:ml-4 lg:sticky top-0 self-start'}>
              <div className={'bg-primary-50 rounded-lg p-3 max-w-[800px] w-full'}>
                <h1 className={'font-semibold text-primary-900 text-lg'}>Order Summary:</h1>
                <Divider className={'my-1'} />
                <div className={'flex justify-between text-sm font-semibold mb-3'}>
                  <p>Subtotal:</p>
                  <p>&#8377; {getTotal()}</p>
                </div>
                <div className={'flex justify-between text-lg font-semibold mt-5'}>
                  <p>Total Amount:</p>
                  <p>&#8377; {getTotal()}</p>
                </div>
              </div>
              <Button
                disabled={isCheckoutDisabled()}
                onClick={() => !isCheckoutDisabled() && navigate('/checkout', {state: {product: cart}})}
                className={'mt-5 group font-semibold w-full h-8 rounded-lg border-none bg-primary-800 text-white hover:!text-primary-900 flex items-center justify-center cursor-pointer transition duration-500 overflow-hidden shadow-md relative active:scale-95 text-sm hover:shadow-[0_0_5px_#3D180F]'}
              >
               Continue to Checkout
              </Button>
              {isCheckoutDisabled() && (
                <p className={'text-[#FF0000] text-sm mt-2'}>
                  Some products in your cart are out of stock. Please remove it.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartScreen;
